
import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { navigation, NavigationItem } from '../constants';
import { SearchBar } from './SearchBar';

export const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false); // Mobile menu toggle
  const [isUtilitiesOpen, setIsUtilitiesOpen] = useState(false); // Mobile utilities dropdown toggle
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const location = useLocation();

  return (
    <motion.nav
      className="fixed top-0 left-0 right-0 bg-zinc-900/95 backdrop-blur-md shadow-lg z-50 border-b border-zinc-900"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] as const }}
    >
      <div className="container mx-auto px-4 py-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center">
            <div className="flex flex-col">
              <motion.div
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                <NavLink to="/" className="flex-shrink-0 text-2xl font-bold bg-gradient-to-r from-sky-400 to-blue-400 bg-clip-text text-transparent hover:from-sky-300 hover:to-blue-300 transition-all duration-300">
                  SANGAM PANTA
                </NavLink>
              </motion.div>
              <div className="hidden md:flex items-center text-xs text-gray-400 space-x-2">
                <span>| Sound Designer | Music Composer | Film Projects Supervisor | Cinema Administrator |</span>
              </div>
            </div>
          </div>
          <div className="hidden md:flex items-center space-x-8">
            <div className="flex items-baseline space-x-4">
              {navigation.map((item, index) => (
                item.children ? (
                  // Desktop Dropdown for items with children
                  <motion.div
                    key={item.name}
                    className="relative group"
                    onMouseEnter={() => setActiveDropdown(item.name)}
                    onMouseLeave={() => setActiveDropdown(null)}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <motion.button
                      type="button"
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-300 flex items-center ${'text-gray-200 hover:bg-zinc-800 hover:text-white hover:shadow-lg'
                        }`}
                      aria-haspopup="true"
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {item.name}
                      <motion.svg
                        className="ml-2 -mr-1 h-5 w-5 text-gray-400 group-hover:text-white"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                        animate={{ rotate: 0 }}
                        whileHover={{ rotate: 180 }}
                        transition={{ duration: 0.3 }}
                      >
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </motion.svg>
                    </motion.button>
                    <motion.div
                      className={`absolute left-0 top-full w-56 pt-2 z-20 ${activeDropdown === item.name ? 'opacity-100 translate-y-2 pointer-events-auto' : 'opacity-0 translate-y-0 pointer-events-none'
                        }`}
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: activeDropdown === item.name ? 1 : 0, y: activeDropdown === item.name ? 0 : -10 }}
                    >
                      <div className="rounded-xl shadow-2xl bg-zinc-900/95 ring-1 ring-zinc-800 overflow-hidden">
                        <div className="py-2" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
                          {item.children.map((child, childIndex) => (
                            <motion.div
                              key={child.name}
                              initial={{ opacity: 0, x: -8 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: childIndex * 0.05 }}
                            >
                              <NavLink
                                to={child.href}
                                className={({ isActive }) =>
                                  `block px-4 py-2 text-sm text-gray-200 hover:bg-zinc-800/80 hover:text-white transition-all duration-300 ${isActive ? 'bg-zinc-950 text-white border-l-2 border-sky-400' : 'border-l-2 border-transparent'
                                  }`
                                }
                                role="menuitem"
                              >
                                {child.name}
                              </NavLink>
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  </motion.div>
                ) : (
                  // Regular NavLink for items without children
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <motion.div
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <NavLink
                        to={item.href}
                        className={({ isActive }) =>
                          `px-3 py-2 rounded-md text-sm font-medium transition-all duration-300 ${isActive
                            ? 'bg-zinc-950 text-white shadow-lg border-l-2 border-sky-400'
                            : 'text-gray-200 hover:bg-zinc-800 hover:text-white hover:shadow-lg'
                          }`
                        }
                      >
                        {item.name}
                      </NavLink>
                    </motion.div>
                  </motion.div>
                )
              ))}
            </div>
            <div className="ml-6 pl-6 border-l border-zinc-800">
              <SearchBar />
            </div>
          </div>
          <div className="-mr-2 flex items-center md:hidden space-x-2">
            <SearchBar />
            <motion.button
              onClick={() => setIsOpen(!isOpen)}
              type="button"
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-zinc-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-zinc-900 focus:ring-white"
              aria-controls="mobile-menu"
              aria-expanded={isOpen}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <span className="sr-only">Open main menu</span>
              <AnimatePresence mode="wait">
                {!isOpen ? (
                  <motion.svg
                    key="menu"
                    className="block h-6 w-6"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                    initial={{ rotate: -180, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 180, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </motion.svg>
                ) : (
                  <motion.svg
                    key="close"
                    className="block h-6 w-6"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                    initial={{ rotate: -180, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 180, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </motion.svg>
                )}
              </AnimatePresence>
            </motion.button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="md:hidden"
            id="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              {navigation.map((item, index) => (
                item.children ? (
                  // Mobile Accordion for items with children
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <motion.button
                      type="button"
                      onClick={() => setIsUtilitiesOpen(!isUtilitiesOpen)}
                      className={`block w-full text-left px-3 py-2 rounded-md text-base font-medium transition-all duration-300 flex justify-between items-center ${isUtilitiesOpen
                        ? 'bg-zinc-800 text-white'
                        : 'text-gray-200 hover:bg-zinc-800 hover:text-white'
                        }`}
                      aria-expanded={isUtilitiesOpen}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {item.name}
                      <motion.svg
                        className={`h-5 w-5 transform ${isUtilitiesOpen ? 'rotate-180' : ''} transition-transform duration-200`}
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                        animate={{ rotate: isUtilitiesOpen ? 180 : 0 }}
                      >
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </motion.svg>
                    </motion.button>
                    <AnimatePresence>
                      {isUtilitiesOpen && (
                        <motion.div
                          className="pl-4 mt-1 space-y-1"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          {item.children.map((child, childIndex) => (
                            <motion.div
                              key={child.name}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: childIndex * 0.05 }}
                            >
                              <NavLink
                                to={child.href}
                                className={({ isActive }) =>
                                  `block px-3 py-2 rounded-md text-sm font-medium transition-all duration-300 ${isActive
                                    ? 'bg-zinc-950 text-white'
                                    : 'text-gray-200 hover:bg-zinc-800 hover:text-white'
                                  }`
                                }
                              >
                                {child.name}
                              </NavLink>
                            </motion.div>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ) : (
                  // Regular NavLink for items without children
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <NavLink
                      to={item.href}
                      onClick={() => setIsOpen(false)}
                      className={({ isActive }) =>
                        `block px-3 py-2 rounded-md text-base font-medium transition-all duration-300 ${isActive
                          ? 'bg-zinc-950 text-white'
                          : 'text-gray-200 hover:bg-zinc-800 hover:text-white'
                        }`
                      }
                    >
                      {item.name}
                    </NavLink>
                  </motion.div>
                )
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};
